import { toggleCompare, isInCompare } from "./compare";
import { STAR_RATING_LABELS, WOULD_TAKE_AGAIN_ENABLED, renderLevelDistribution, renderEaseOfPassingText, attachTapTooltip } from "./generics";

// Data
export interface Faculty {
    id: number;
    name: string;
    slug: string;
    lecturer_count: number;
}

export interface University {
    id: number;
    name: string;
    slug: string;
    // Skróty do wyszukiwarki ("WUM", "PW") - patrz UniversitySerializer.get_aliases; initials -
    // skrót z pierwszych liter nazwy, tylko do dopasowania (nie wyświetlany).
    aliases?: string[];
    initials?: string;
}

// Odpowiedź na dodanie uczelni: albo nowa uczelnia, albo (duplicate_of) propozycja istniejącej -
// patrz UniversityListView.create / _find_duplicate.
export interface UniversityCreateResult extends Partial<University> {
    duplicate_of?: University;
    kind?: "same" | "part_of";
    reason?: string;
}

export interface Post {
    id: number; name: string; surname: string; slug: string, avg_rating: number, number_of_comments: number;
    would_take_again_percent: number | null;
    rating_distribution?: { [key: string]: number };
    // Nazwa głównego wydziału - null gdy w ogóle bez wydziału (placeholder, patrz
    // needs_faculty_confirmation niżej). Pokazywana we froncie tylko w widoku całej uczelni.
    faculty_name?: string | null;
    // Surowe ID wydziału (nie tylko nazwa/slug) - potrzebne do api.getSubjects(faculty).
    // Zawsze obecne w odpowiedzi PostSerializer (patrz pages/api/serializers.py).
    faculty?: number;
    // Czy ta osoba wciąż siedzi w technicznym koszyku "Bez przypisanego wydziału" (patrz
    // PLACEHOLDER_FACULTY_SLUG w pages/api/posts.py) - front (patrz renderAddPersonForm w
    // addLecturer.ts) tego używa, żeby z odpowiedzi o całej uczelni wyłuskać kandydatów do
    // podpowiedzi w popupie "Dodaj osobę".
    needs_faculty_confirmation?: boolean;
    // Wymiar egzaminu (patrz Comment.exam_* w models.py) - wystawione tylko przez
    // PostWithRatingDistributionSerializer (profil, porównanie), NIE zwykły PostSerializer
    // (lista wykładowców) - stąd wszystkie opcjonalne. supports_exam_dimensions steruje tym, czy
    // compareView.ts w ogóle pokazuje przełącznik Zajęcia/Egzamin dla danej karty.
    supports_exam_dimensions?: boolean;
    avg_exam_rating?: number | null;
    number_of_exam_comments?: number;
    exam_would_take_again_percent?: number | null;
    exam_rating_distribution?: { [key: string]: number };
    // Trudność zajęć/egzaminu (patrz renderEaseOfPassingText w generics.ts) - jak avg_exam_rating
    // wyżej, wystawione tylko przez PostWithRatingDistributionSerializer (exclude=['ip_address']
    // wystawia WSZYSTKIE pola modelu, więc to już jest w odpowiedzi API, tylko brakowało go w tym
    // typie).
    avg_class_difficulty?: number | null;
    avg_exam_difficulty?: number | null;
}

export type PostSortField  = "surname" | "name" | "rating" | "most_comments" | "power" | "exam_rating" | "most_exam_comments";

// Zajęcia/Egzamin - używane przez renderPost (karta na liście, patrz browser.ts) i mirror w
// compareView.ts (tam lokalny DimensionTab, nie eksportowany stąd - inny kontekst: karta
// porównania kontra kafelek listy).
export type PostDimension = "zajecia" | "egzamin";


// Functions

// "Moc" - bayesowska (ważona) średnia ocen, uwzględniająca też ich liczbę - surowa średnia
// (case "rating" niżej) nie odróżnia 20 opinii na 4.5 od 2 opinii na 5.0, mimo że ta pierwsza
// ocena jest dużo pewniejsza. To ten sam wzór, którego IMDB używa do rankingu "Top 250":
//   WR = (v / (v+m)) * R + (m / (v+m)) * C
// gdzie R - średnia ocen wykładowcy, v - liczba jego opinii, C - średnia ocen w całej aktualnie
// sortowanej grupie, m - "próg zaufania" (tu: średnia liczba opinii na wykładowcę w tej grupie).
// Im mniej opinii dany wykładowca ma względem tego progu, tym mocniej jego ocena "ściąga" w
// stronę C zamiast być brana za dobrą monetę - to formalnie średnia a posteriori przy
// (uproszczonym) założeniu, że oceny każdego wykładowcy pochodzą z rozkładu o średniej C, dopóki
// nie zbierze się wystarczająco dużo jego własnych opinii, żeby ją przeważyć.
function powerScore(post: Post, globalMean: number, priorWeight: number): number {
    const v = post.number_of_comments;
    const denominator = v + priorWeight;
    if (denominator === 0) return globalMean;
    return (v / denominator) * post.avg_rating + (priorWeight / denominator) * globalMean;
}

export function sortPosts(posts: Post[], by: PostSortField): Post[] {
    const sorted = [...posts];

    if (by === "power") {
        const totalComments = sorted.reduce((sum, p) => sum + p.number_of_comments, 0);
        const globalMean = totalComments > 0
            ? sorted.reduce((sum, p) => sum + p.avg_rating * p.number_of_comments, 0) / totalComments
            : 0;
        const priorWeight = sorted.length > 0 ? totalComments / sorted.length : 0;

        sorted.sort((a, b) => powerScore(b, globalMean, priorWeight) - powerScore(a, globalMean, priorWeight));
        return sorted;
    }

    sorted.sort((a, b) => {
        switch (by) {
            case "surname":
                return a.surname.localeCompare(b.surname);
            case "name":
                return a.name.localeCompare(b.name);
            case "rating":
                // If posts have the same rating, we compare it by number of votes
                let rating_diff = b.avg_rating - a.avg_rating;
                if (rating_diff != 0) return rating_diff;
                else return b.number_of_comments - a.number_of_comments;
            case "most_comments":
                return b.number_of_comments - a.number_of_comments;
            // Odpowiednik "rating"/"most_comments" wyżej, dla wymiaru egzaminu - używane tylko w
            // porównaniu (compareView.ts) na zakładce "Egzamin" (0/null traktowane jak "brak
            // informacji", na koniec listy).
            case "exam_rating":
                let exam_rating_diff = (b.avg_exam_rating ?? 0) - (a.avg_exam_rating ?? 0);
                if (exam_rating_diff != 0) return exam_rating_diff;
                else return (b.number_of_exam_comments ?? 0) - (a.number_of_exam_comments ?? 0);
            case "most_exam_comments":
                return (b.number_of_exam_comments ?? 0) - (a.number_of_exam_comments ?? 0);
            default:
                return 0;
        }
    });

    return sorted;
}
// Gwiazdki dla ŚREDNIEJ oceny - wypełnione ciągle (z dokładnością do 1/10), a nie tylko całymi
// gwiazdkami jak showStars() - inaczej 4.1 i 4.9 wyglądałyby identycznie (obie jako 4 gwiazdki).
// Eksportowana - używana też przez renderExamHeadline/renderExamSummaryContent dla oceny
// egzaminu (Comment.exam_rating), ten sam widget gwiazdkowy, tylko inne etykiety.
export function showPartialStars(rating: number): HTMLElement {
    const container = document.createElement("div");
    container.className = "star-rating star-rating-partial";

    for (let i = 1; i <= 5; i++) {
        const rawFraction = Math.max(0, Math.min(1, rating - (i - 1)));
        const tenthFraction = Math.round(rawFraction * 10) / 10;

        const starWrap = document.createElement("span");
        starWrap.className = "star-partial";

        const starBg = document.createElement("span");
        starBg.className = "star-partial-bg";
        starBg.innerHTML = "&#9733;";

        const starFg = document.createElement("span");
        starFg.className = "star-partial-fg";
        starFg.style.width = `${tenthFraction * 100}%`;
        starFg.innerHTML = "&#9733;";

        starWrap.append(starBg, starFg);
        container.appendChild(starWrap);
    }

    return container;
}

// Odznaka "N% wybrałoby ponownie" obok gwiazdek - null, gdy jeszcze nikt nie odpowiedział
// na to pytanie (zamiast mylącego "0%"). Funkcjonalność jest na razie schowana (patrz
// WOULD_TAKE_AGAIN_ENABLED w generics.ts) - schowana tu w jednym miejscu, żeby ukryć ją od
// razu we wszystkich miejscach, gdzie ta odznaka się pojawia (lista, profil, porównanie).
export function renderWouldTakeAgainBadge(percent: number | null | undefined): HTMLElement | null {
    if (!WOULD_TAKE_AGAIN_ENABLED) return null;
    if (percent === null || percent === undefined) return null;

    const badge = document.createElement("span");
    badge.className = "would-take-again-badge";
    badge.textContent = `👍 ${Math.round(percent)}%`;
    attachTapTooltip(badge, "Odsetek osób, które polecają tego wykładowcę");
    return badge;
}

// Koloruje TYLKO skrajne wartości (bardzo dobra/bardzo słaba średnia) - środek zostaje
// neutralny (var(--text-color)), żeby lista nie zrobiła się "choinką". Osobny sygnał od
// pomarańczowych gwiazdek (te mają stały kolor niezależnie od wartości oceny - oznaczają
// "to jest ocena", nie "ta ocena jest dobra/zła"). 0 (jeszcze bez opinii) celowo nie jest
// traktowane jako "słaba ocena".
function ratingTierClass(rating: number): string {
    if (rating >= 4) return "rating-tier-good";
    if (rating > 0 && rating < 2.5) return "rating-tier-poor";
    return "";
}

export function showAvgRating(
    average_rating: number, number_of_comments: number, would_take_again_percent?: number | null
): HTMLElement {
    const avgRatingElement = document.createElement("div");
    // flex + gap (nie spacja w tekście) - spacja na końcu treści inline-block elementu (patrz
    // poprzednia wersja: "2.8 " jako ostatni węzeł tekstowy w <span>) jest przez przeglądarkę
    // traktowana jak biały znak na końcu wiersza i wizualnie znika, więc mimo obecności w DOM
    // odstęp przed gwiazdkami był niewidoczny (w odróżnieniu od odstępu PO gwiazdkach, przed
    // "(N)", który jest spacją na POCZĄTKU treści kolejnego elementu - tej przeglądarka nie ucina).
    // gap gwarantuje identyczny, faktycznie widoczny odstęp po obu stronach gwiazdek.
    avgRatingElement.style.display = "inline-flex";
    avgRatingElement.style.alignItems = "center";
    avgRatingElement.style.gap = "0.3em";
    // Patrz .avg-rating-row w style.css - pilnuje, żeby w wąskich miejscach (karty porównania)
    // liczba/gwiazdki/licznik nie łamały się w środku ("4." | "0").
    avgRatingElement.classList.add("avg-rating-row");

    const avgRatingNumberContainer = document.createElement("span");
    avgRatingNumberContainer.textContent = average_rating.toPrecision(2);
    avgRatingNumberContainer.style.textDecoration = "none";
    avgRatingNumberContainer.className = ratingTierClass(average_rating);

    const stars: HTMLElement = showPartialStars(average_rating);

    const number_of_comments_container = document.createElement("span");
    number_of_comments_container.textContent = `(${number_of_comments})`;
    number_of_comments_container.style.textDecoration = "none";

    avgRatingElement.appendChild(avgRatingNumberContainer);
    avgRatingElement.appendChild(stars);
    avgRatingElement.appendChild(number_of_comments_container);

    const badge = renderWouldTakeAgainBadge(would_take_again_percent);
    if (badge) avgRatingElement.appendChild(badge);

    return avgRatingElement;
}

// Rozkład ocen (ile 5*, 4*, itd.) jak w Google Maps - poziome słupki. Pełna wersja (profil
// wykładowcy) pokazuje opisy ocen (Wybitny/Świetny/...), wersja w porównaniu - same liczby,
// bo kafle są tam wąskie.
export function renderRatingDistribution(
    distribution: { [key: string]: number },
    options?: { showLabels?: boolean; labels?: { [key: number]: string } }
): HTMLElement {
    return renderLevelDistribution(distribution, options?.labels ?? STAR_RATING_LABELS, options);
}

// Podsumowanie oceny w stylu Google Maps - rozkład (paski) po lewej, po prawej duża liczba,
// gwiazdki i "Opinie: N". Używane na profilu wykładowcy zamiast małego, liniowego showAvgRating.
export function renderRatingSummary(
    average_rating: number,
    number_of_comments: number,
    distribution: { [key: string]: number },
    would_take_again_percent?: number | null,
    // Trudność zajęć/egzaminu (patrz Post.avg_class_difficulty/avg_exam_difficulty wyżej) - opcjonalna
    // (undefined/null pomija badge), bo dotyczy na razie tylko WPiA (patrz post_detail.ts/
    // compareView.ts, jedyni wołający, którzy ją przekazują).
    avg_difficulty?: number | null
): HTMLElement {
    const container = document.createElement("div");
    container.className = "rating-summary";

    const bars = renderRatingDistribution(distribution, { showLabels: true });
    bars.classList.add("rating-summary-bars");

    const headline = document.createElement("div");
    headline.className = "rating-summary-headline";

    const number = document.createElement("div");
    number.className = `rating-summary-number ${ratingTierClass(average_rating)}`.trim();
    number.textContent = average_rating.toFixed(1).replace(".", ",");

    const stars = showPartialStars(average_rating);
    stars.classList.add("rating-summary-stars");

    const count = document.createElement("div");
    count.className = "rating-summary-count";
    count.textContent = `Opinie: ${number_of_comments}`;

    headline.append(number, stars, count);

    const badge = renderWouldTakeAgainBadge(would_take_again_percent);
    if (badge) {
        badge.classList.add("rating-summary-would-take-again");
        headline.appendChild(badge);
    }

    // .rating-summary-headline jest flex-direction:column - każde dokładane tu dziecko ląduje
    // automatycznie w swojej własnej linii, POD poprzednimi (numer -> gwiazdki -> "Opinie: N" ->
    // "poleca" -> to) - stąd to trafia "pod ocenami" (na wyraźną prośbę) bez żadnego dodatkowego
    // pozycjonowania.
    if (avg_difficulty !== undefined && avg_difficulty !== null) {
        const difficultyBadge = renderEaseOfPassingText(avg_difficulty);
        if (difficultyBadge) {
            difficultyBadge.classList.add("rating-summary-difficulty");
            headline.appendChild(difficultyBadge);
        }
    }

    container.append(headline, bars);
    return container;
}

// Ustawia wygląd przycisku "Porównaj" wg aktualnego stanu koszyka (id posta w data-post-id).
function syncCompareButton(btn: HTMLButtonElement): void {
    const postId = Number(btn.dataset.postId);
    const active = isInCompare(postId);
    btn.classList.toggle("active", active);
    btn.innerHTML = active
        ? `<span class="add-comment-btn-icon" aria-hidden="true">&#10003;</span> Porównywany`
        : `<span class="add-comment-btn-icon" aria-hidden="true">+</span> Porównaj`;
}

// Wywoływane po każdej zmianie koszyka porównania (np. "Usuń" na pływającym pasku, albo "Usuń
// z porównania" na stronie /porownaj), żeby WSZYSTKIE przyciski "Porównaj" na aktualnej stronie
// (lista i widok wykładowcy) od razu odzwierciedlały nowy stan - bez tego zaznaczenie zostałoby
// "wiszące" do czasu ręcznego odświeżenia. Rejestrujemy raz na moduł - importujące strony
// (browser.ts, post_detail.ts) dostają to "za darmo", bez własnego nasłuchiwania.
export function refreshCompareButtons(): void {
    document.querySelectorAll<HTMLButtonElement>(".compare-toggle-btn").forEach(syncCompareButton);
}
window.addEventListener("compare-changed", refreshCompareButtons);
// "storage" - zmiana koszyka w INNEJ karcie (localStorage nie budzi karty, w której nastąpiła
// zmiana, tylko pozostałe). "pageshow" z persisted=true - powrót do tej strony z bfcache
// (np. przyciskiem "Wstecz" po usunięciu porównania na /porownaj) przywraca zamrożony,
// nieodświeżony DOM sprzed nawigacji, bez ponownego uruchomienia skryptów - ten sam wzorzec
// co przy #compare-bar, patrz compareBar.ts.
window.addEventListener("storage", refreshCompareButtons);
window.addEventListener("pageshow", refreshCompareButtons);

export function createCompareButton(postId: number): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "add-comment-btn compare-toggle-btn";
    btn.dataset.postId = String(postId);
    syncCompareButton(btn);

    btn.addEventListener("click", (event) => {
        // Może się znaleźć wewnątrz <a> (karta na liście) - trzeba zablokować nawigację.
        event.preventDefault();
        event.stopPropagation();
        toggleCompare(postId);
    });

    return btn;
}

// originFacultySlug - slug wydziału, z którego listy renderuje się ten link (patrz browser.ts) -
// dopisany jako ?wydzial= pozwala profilowi (patrz preselectFacultySlug w post_detail.ts) wiedzieć,
// skąd trafił tu odwiedzający, i podpowiedzieć to w pickerze wydziału przy jeszcze nierozstrzygniętym
// wydziale (patrz renderFacultyPicker w comments.ts). Pominięty (undefined), gdy lista nie jest
// zawężona do konkretnego wydziału (np. cała uczelnia/strona główna) - wtedy nie ma czego sensownie
// podpowiedzieć.
// showFacultyName - patrz Post.faculty_name - włączane tylko w widoku CAŁEJ uczelni (bez
// zawężenia do jednego wydziału, patrz browser.ts), gdzie inaczej nic na karcie nie mówi, z
// jakiego wydziału jest dana osoba. Każda osoba widoczna na liście ma co najmniej jeden komentarz
// (patrz filtr number_of_comments w browser.ts), więc jej wydział jest zawsze pewny - bez
// wcześniejszego rozróżniania "potwierdzony/kandydat".
// dimension - przełącznik Zajęcia/Egzamin na liście wykładowców (patrz browser.ts), na razie
// tylko WPiA UW - pokazuje avg_exam_rating/number_of_exam_comments zamiast avg_rating/
// number_of_comments, ten sam showAvgRating (dimension-agnostic, bierze gotowe liczby).
export function renderPost(
    post: Post, originFacultySlug?: string, showFacultyName?: boolean, dimension: PostDimension = "zajecia"
): HTMLElement {
    const card = document.createElement("a");
    card.className = "comment-card";
    card.dataset.id = String(post.id);
    const profileHref = originFacultySlug ? `/${post.slug}?wydzial=${encodeURIComponent(originFacultySlug)}` : `/${post.slug}`;
    card.href = profileHref;

    const heading = document.createElement("h2");
    heading.textContent = `${post.name} ${post.surname}`;

    card.append(heading);

    if (showFacultyName && post.faculty_name) {
        const facultyLine = document.createElement("p");
        facultyLine.className = "comment-card-faculty";
        facultyLine.textContent = post.faculty_name;
        card.append(facultyLine);
    }

    // Puste gwiazdki (zawsze 0/5) wyglądałyby jak realna, słaba ocena, więc dla braku opinii
    // pokazujemy neutralny, statyczny tekst zamiast nich - bez żadnej interakcji na tym kafelku
    // (był tu wcześniej hover->"Dodaj opinię" z popupem, świadomie usunięty - na telefonie hover
    // nie istnieje, więc nic tego nie zapowiadało, a sama funkcja niepotrzebnie komplikowała
    // kartę). Dodanie pierwszej opinii tej osobie idzie teraz wyłącznie przez jej profil.
    const meta = document.createElement("div");
    meta.className = "comment-card-meta";
    const isExam = dimension === "egzamin";
    const numComments = isExam ? (post.number_of_exam_comments ?? 0) : post.number_of_comments;
    if (numComments > 0) {
        const stars = isExam
            ? showAvgRating(post.avg_exam_rating ?? 0, numComments, post.exam_would_take_again_percent)
            : showAvgRating(post.avg_rating, numComments, post.would_take_again_percent);
        const compareBtn = createCompareButton(post.id);
        meta.append(stars, compareBtn);
    } else {
        const noReviews = document.createElement("span");
        noReviews.className = "comment-card-no-reviews";
        noReviews.textContent = isExam ? "Brak opinii o egzaminie" : "Brak opinii";
        meta.append(noReviews);
    }
    card.append(meta);

    return card;
}
